<template>
  <div class="pool-performance-chart">
    <v-card class="mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-chart-line</v-icon>
        Desempenho Histórico
        <v-spacer></v-spacer>
        <v-select
          v-model="selectedMetric"
          :items="metrics"
          label="Métrica"
          density="compact"
          variant="outlined"
          hide-details
          class="select-width"
        ></v-select>
        <v-select
          v-model="selectedTimeframe"
          :items="timeframes"
          label="Período"
          density="compact"
          variant="outlined"
          hide-details
          class="select-width ml-4"
        ></v-select>
      </v-card-title>
      <v-card-text>
        <canvas ref="performanceChart" height="300"></canvas>
        
        <div class="d-flex flex-wrap align-center justify-center gap-4 pt-4 mt-4 border-top">
          <div class="stat-box py-2 px-4 text-center">
            <div class="text-overline mb-1">APR Atual</div>
            <div class="text-h5 font-weight-bold" :class="currentStats.aprChange >= 0 ? 'text-success' : 'text-error'">
              {{ formatPercent(currentStats.apr) }}
            </div>
            <div class="text-caption" :class="currentStats.aprChange >= 0 ? 'text-success' : 'text-error'">
              {{ currentStats.aprChange >= 0 ? '+' : '' }}{{ formatPercent(currentStats.aprChange) }} nos últimos 7d
            </div>
          </div>
          
          <div class="stat-box py-2 px-4 text-center">
            <div class="text-overline mb-1">Volume 24h</div>
            <div class="text-h5 font-weight-bold">${{ formatNumber(currentStats.volume24h) }}</div>
            <div class="text-caption" :class="currentStats.volumeChange >= 0 ? 'text-success' : 'text-error'">
              {{ currentStats.volumeChange >= 0 ? '+' : '' }}{{ formatPercent(currentStats.volumeChange) }} vs dia anterior
            </div>
          </div>
          
          <div class="stat-box py-2 px-4 text-center">
            <div class="text-overline mb-1">TVL</div>
            <div class="text-h5 font-weight-bold">${{ formatNumber(currentStats.tvl) }}</div>
            <div class="text-caption" :class="currentStats.tvlChange >= 0 ? 'text-success' : 'text-error'">
              {{ currentStats.tvlChange >= 0 ? '+' : '' }}{{ formatPercent(currentStats.tvlChange) }} nos últimos 7d
            </div>
          </div>
          
          <div class="stat-box py-2 px-4 text-center">
            <div class="text-overline mb-1">Impermanent Loss</div>
            <div class="text-h5 font-weight-bold" :class="currentStats.ilValue <= 2 ? 'text-success' : currentStats.ilValue <= 5 ? 'text-warning' : 'text-error'">
              {{ formatPercent(currentStats.ilValue) }}
            </div>
            <div class="text-caption">
              {{ currentStats.ilValue <= 2 ? 'Baixo' : currentStats.ilValue <= 5 ? 'Moderado' : 'Alto' }} risco
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
    
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-history</v-icon>
        Histórico de Recompensas
        <v-spacer></v-spacer>
        <v-select
          v-model="rewardsViewMode"
          :items="[
            { title: 'Tabela', value: 'table' },
            { title: 'Gráfico', value: 'chart' }
          ]"
          density="compact"
          variant="outlined"
          hide-details
          class="select-width"
        ></v-select>
      </v-card-title>
      <v-card-text>
        <!-- Tabela de recompensas -->
        <div v-if="rewardsViewMode === 'table'">
          <v-data-table
            :headers="rewardsHeaders"
            :items="rewardsHistory"
            :items-per-page="5"
            class="rewards-table"
          >
            <!-- Data da recompensa -->
            <template v-slot:item.date="{ item }">
              {{ formatDate(item.raw.date) }}
            </template>
            
            <!-- Valor da recompensa -->
            <template v-slot:item.amount="{ item }">
              <div>
                <div>{{ formatNumber(item.raw.amount) }} {{ coin }}</div>
                <div class="text-caption">${{ formatNumber(item.raw.valueUsd) }}</div>
              </div>
            </template>
            
            <!-- APR na data -->
            <template v-slot:item.apr="{ item }">
              <span :class="item.raw.apr >= 15 ? 'text-success font-weight-bold' : ''">
                {{ formatPercent(item.raw.apr) }}
              </span>
            </template>
            
            <!-- Tipo de recompensa -->
            <template v-slot:item.type="{ item }">
              <v-chip
                :color="getRewardTypeColor(item.raw.type)"
                size="small"
                class="text-uppercase"
              >
                <v-icon start size="x-small">{{ getRewardTypeIcon(item.raw.type) }}</v-icon>
                {{ item.raw.type }}
              </v-chip>
            </template>
          </v-data-table>
        </div>
        
        <!-- Gráfico de recompensas -->
        <div v-else>
          <canvas ref="rewardsChart" height="250"></canvas>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'

export default {
  name: 'PoolPerformanceChart',
  
  props: {
    poolId: {
      type: [Number, String],
      required: true
    },
    token1: {
      type: String,
      required: true
    },
    token2: {
      type: String,
      required: true
    }
  },
  
  data() {
    return {
      selectedMetric: 'apr',
      selectedTimeframe: '30d',
      rewardsViewMode: 'table',
      performanceChart: null,
      rewardsChart: null,
      
      metrics: [
        { title: 'APR', value: 'apr' },
        { title: 'Volume', value: 'volume' },
        { title: 'TVL', value: 'tvl' },
        { title: 'Preço', value: 'price' }
      ],
      
      timeframes: [
        { title: '7 dias', value: '7d' },
        { title: '30 dias', value: '30d' },
        { title: '90 dias', value: '90d' },
        { title: '1 ano', value: '1y' },
        { title: 'Tudo', value: 'all' }
      ],
      
      rewardsHeaders: [
        { title: 'Data', key: 'date', align: 'start' },
        { title: 'Recompensa', key: 'amount', align: 'end' },
        { title: 'APR', key: 'apr', align: 'end' },
        { title: 'Tipo', key: 'type', align: 'center' }
      ],
      
      // Mock data para desenvolvimento
      historicalData: {
        apr: generateHistoricalDataPoints(30, 15, 25),
        volume: generateHistoricalDataPoints(30, 100000, 500000),
        tvl: generateHistoricalDataPoints(30, 1000000, 3000000),
        price: generateHistoricalDataPoints(30, 2, 3)
      },
      
      rewardsHistory: [
        { date: new Date('2025-04-21'), amount: 5.8, valueUsd: 14.5, apr: 24.5, type: 'fees' },
        { date: new Date('2025-04-14'), amount: 6.2, valueUsd: 15.5, apr: 23.8, type: 'fees' },
        { date: new Date('2025-04-07'), amount: 4.9, valueUsd: 12.25, apr: 22.1, type: 'fees' },
        { date: new Date('2025-03-31'), amount: 7.1, valueUsd: 17.75, apr: 25.3, type: 'fees' },
        { date: new Date('2025-03-24'), amount: 8.4, valueUsd: 21.0, apr: 26.7, type: 'incentive' },
        { date: new Date('2025-03-17'), amount: 5.2, valueUsd: 13.0, apr: 22.5, type: 'fees' },
        { date: new Date('2025-03-10'), amount: 4.7, valueUsd: 11.75, apr: 21.8, type: 'fees' },
        { date: new Date('2025-03-03'), amount: 12.5, valueUsd: 31.25, apr: 28.4, type: 'incentive' }
      ],
      
      currentStats: {
        apr: 24.5,
        aprChange: 1.2,
        volume24h: 325640.50,
        volumeChange: 5.3,
        tvl: 950235.75,
        tvlChange: 2.8,
        ilValue: 2.5
      }
    }
  },
  
  watch: {
    selectedMetric() {
      this.updatePerformanceChart()
    },
    
    selectedTimeframe() {
      this.updatePerformanceChart()
    },
    
    rewardsViewMode(newValue) {
      if (newValue === 'chart' && !this.rewardsChart) {
        this.$nextTick(() => {
          this.initRewardsChart()
        })
      }
    }
  },
  
  methods: {
    formatNumber(value) {
      return parseFloat(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    formatPercent(value) {
      return parseFloat(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + '%'
    },
    
    formatDate(date) {
      return new Date(date).toLocaleDateString('pt-BR')
    },
    
    getRewardTypeColor(type) {
      return type === 'fees' ? 'primary' : type === 'incentive' ? 'success' : 'warning'
    },
    
    getRewardTypeIcon(type) {
      return type === 'fees' ? 'mdi-currency-usd' : type === 'incentive' ? 'mdi-gift-outline' : 'mdi-star'
    },
    
    initPerformanceChart() {
      const ctx = this.$refs.performanceChart.getContext('2d')
      
      this.performanceChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: [], // Será preenchido pelo updatePerformanceChart
          datasets: [{
            label: 'APR (%)',
            data: [],
            borderColor: '#6200ea',
            backgroundColor: 'rgba(98, 0, 234, 0.1)',
            tension: 0.4,
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || ''
                  if (label) {
                    label += ': '
                  }
                  if (context.parsed.y !== null) {
                    if (context.dataset.label.includes('%')) {
                      label += context.parsed.y.toFixed(2) + '%'
                    } else if (context.dataset.label.includes('$')) {
                      label += '$' + context.parsed.y.toLocaleString('pt-BR')
                    } else {
                      label += context.parsed.y.toLocaleString('pt-BR')
                    }
                  }
                  return label
                }
              }
            }
          },
          scales: {
            x: {
              display: true,
              grid: {
                display: false
              }
            },
            y: {
              display: true,
              beginAtZero: false
            }
          }
        }
      })
      
      this.updatePerformanceChart()
    },
    
    updatePerformanceChart() {
      if (!this.performanceChart) return
      
      // Determinar dados com base no período selecionado
      let data = this.historicalData[this.selectedMetric] || []
      let timeframeDays = 30
      
      switch (this.selectedTimeframe) {
        case '7d':
          timeframeDays = 7
          data = data.slice(-7)
          break
        case '30d':
          timeframeDays = 30
          data = data.slice(-30)
          break
        case '90d':
          timeframeDays = 90
          data = data.slice(-90)
          break
        case '1y':
          timeframeDays = 365
          data = data.slice(-365)
          break
        default:
          break
      }
      
      // Gerar labels de datas
      const today = new Date()
      const labels = Array.from({ length: data.length }, (_, i) => {
        const date = new Date(today)
        date.setDate(date.getDate() - (data.length - 1 - i))
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
      })
      
      // Atualizar gráfico
      this.performanceChart.data.labels = labels
      this.performanceChart.data.datasets[0].data = data
      
      // Atualizar label e formatação adequada para a métrica selecionada
      switch (this.selectedMetric) {
        case 'apr':
          this.performanceChart.data.datasets[0].label = 'APR (%)'
          this.performanceChart.data.datasets[0].borderColor = '#6200ea'
          this.performanceChart.data.datasets[0].backgroundColor = 'rgba(98, 0, 234, 0.1)'
          break
        case 'volume':
          this.performanceChart.data.datasets[0].label = 'Volume ($)'
          this.performanceChart.data.datasets[0].borderColor = '#00bcd4'
          this.performanceChart.data.datasets[0].backgroundColor = 'rgba(0, 188, 212, 0.1)'
          break
        case 'tvl':
          this.performanceChart.data.datasets[0].label = 'TVL ($)'
          this.performanceChart.data.datasets[0].borderColor = '#4caf50'
          this.performanceChart.data.datasets[0].backgroundColor = 'rgba(76, 175, 80, 0.1)'
          break
        case 'price':
          this.performanceChart.data.datasets[0].label = `Preço ${this.token1}/${this.token2}`
          this.performanceChart.data.datasets[0].borderColor = '#ff9800'
          this.performanceChart.data.datasets[0].backgroundColor = 'rgba(255, 152, 0, 0.1)'
          break
      }
      
      this.performanceChart.update()
    },
    
    initRewardsChart() {
      if (!this.$refs.rewardsChart) return
      
      const ctx = this.$refs.rewardsChart.getContext('2d')
      
      // Preparar dados
      const labels = this.rewardsHistory.map(r => this.formatDate(r.date)).reverse()
      const amounts = this.rewardsHistory.map(r => r.amount).reverse()
      const types = this.rewardsHistory.map(r => r.type).reverse()
      
      // Separar por tipo de recompensa para diferentes cores
      const feesData = this.rewardsHistory.map((r, i) => r.type === 'fees' ? r.amount : 0).reverse()
      const incentiveData = this.rewardsHistory.map((r, i) => r.type === 'incentive' ? r.amount : 0).reverse()
      
      this.rewardsChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'Taxas',
              data: feesData,
              backgroundColor: 'rgba(98, 0, 234, 0.7)',
              borderColor: 'rgba(98, 0, 234, 1)',
              borderWidth: 1
            },
            {
              label: 'Incentivos',
              data: incentiveData,
              backgroundColor: 'rgba(0, 200, 83, 0.7)',
              borderColor: 'rgba(0, 200, 83, 1)',
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || ''
                  if (label) {
                    label += ': '
                  }
                  if (context.parsed.y !== null) {
                    label += context.parsed.y.toFixed(2) + ' ' + (window.__QUARNX_COIN__ || 'tKinX')
                  }
                  return label
                }
              }
            }
          },
          scales: {
            x: {
              stacked: true
            },
            y: {
              stacked: true
            }
          }
        }
      })
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      this.initPerformanceChart()
    })
  },
  
  beforeDestroy() {
    if (this.performanceChart) {
      this.performanceChart.destroy()
    }
    if (this.rewardsChart) {
      this.rewardsChart.destroy()
    }
  }
}

// Função helper para gerar dados aleatórios para desenvolvimento
function generateHistoricalDataPoints(days, min, max) {
  return Array.from({ length: days }, () => min + Math.random() * (max - min))
}
</script>

<style scoped>
.pool-performance-chart {
  width: 100%;
}

.select-width {
  max-width: 180px;
}

.stat-box {
  min-width: 150px;
  border-radius: 8px;
  background-color: rgba(var(--v-theme-surface-variant), 0.5);
}

.border-top {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.rewards-table {
  margin-top: 16px;
}
</style>
